import { FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { isDateTimeField, matchesFieldKey, normaliseKey } from "./preselectFieldMatching";

// The localized titles SharePoint gives the "empty value" bucket, one entry per
// locale the product ships (en/nl/de). Exported as the single source of truth —
// FileFilterHelper and CMFileFilterPanelSection consume this predicate too, so a
// new locale's title is added exactly once.
const EMPTY_ONLY_TITLES = ["(Empty)", "(Leeg)", "(Leer)"];

export const isEmptyOnlyTitle = (title: string): boolean => EMPTY_ONLY_TITLES.includes(title);

/**
 * Post-build display pruning for the filter panel, extracted from
 * setFilterSectionsFromFields so it stays a pure function: trivial sections —
 * fewer than two items, which covers zero-item, single-value and "(Empty)"-only
 * sections alike via the final length filter — are hidden because they offer no
 * meaningful choice in the panel.
 *
 * `keepFieldKeys` (pre-select querystring keys, matched like every other
 * pre-select key resolution — see preselectFieldMatching) exempts the fields a
 * deep link filters on. Pruning those is not cosmetic: the reconcile's
 * fast-vs-reconciled signature comparison reads the lost selection as a query
 * change and fires a corrective re-fetch whose sections no longer carry the
 * filter — a second, UNFILTERED load. A deep-linked field must therefore
 * survive pruning even when its value list is trivial (e.g. a folder where
 * every document shares one value).
 */
export const buildDisplayFilterSections = (
    builtSections: FilterSection[],
    keepFieldKeys?: string[],
): FilterSection[] => {
    const keptKeys = (keepFieldKeys ?? []).map(normaliseKey);
    // DateTime is excluded from the keep exemption: the resolver drops DateTime
    // keys and the synthesizer skips them, so a kept DateTime section can never
    // carry a pre-select selection — pruning it cannot cause the signature
    // mismatch the exemption protects against, while keeping it resurrects
    // trivial (or empty, slider-only) sections the legacy pruning hid.
    const isKeptField = (field: any): boolean => !isDateTimeField(field) && keptKeys.some(key => matchesFieldKey(field, key));

    const displayFilterSections: FilterSection[] = [];

    builtSections
        ?.filter(section => section)
        ?.forEach(section => {
            if (!(section.filterField.RealFieldName === 'ID' && section.filterField.FromBaseType === 'TRUE')) {
                if (section.items?.length === 1) {
                    // A single-item section renders no slider; whether it survives at
                    // all is decided solely by the final length/kept filter below.
                    section.showSlider = false;
                    section.hasSlider = false;
                    displayFilterSections.push(section);
                }
                else if (section.items?.length === 2) {
                    // Hide section slider if only 1 item
                    if (isEmptyOnlyTitle(section.items[0].title)) {
                        section.showSlider = false;
                        section.hasSlider = false;
                    }
                    displayFilterSections.push(section);
                }
                else {
                    displayFilterSections.push(section);
                }
            }
        });

    const forceToFrontFieldName: string = "ContentType";
    const forceToFrontIndex: number = displayFilterSections.findIndex(x => x?.filterField?.RealFieldName === forceToFrontFieldName);
    if (forceToFrontIndex !== -1) {
        displayFilterSections.unshift(displayFilterSections.splice(forceToFrontIndex, 1)[0]);
    }
    return displayFilterSections.filter(section => section?.items?.length > 1 || isKeptField(section?.filterField));
};
